import dayjs from 'dayjs'
import { isEmpty, isNil } from 'lodash'
import { useCallback, useMemo } from 'react'

import { ProductBoardItemsFilterInput } from '~generated/types'
import { pickValidFields } from '~lib/helpers/pickValidFields'
import usePersistentState from '~lib/hooks/usePersistentState'

type UseProductBoardItemsFilterParams = {
  key: string
  defaultState: ProductBoardItemsFilterInput | (() => ProductBoardItemsFilterInput)
}

function removeEmptyFields<T extends Record<string, any> = Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (!isEmpty(value) || dayjs(value).isValid()) acc[key] = value
    return acc
  }, {})
}

function useProductBoardItemsFilter({ key, defaultState }: UseProductBoardItemsFilterParams) {
  const [persistentFilter, setPersistentFilter] = usePersistentState<ProductBoardItemsFilterInput>({
    key: `productBoardItemsFilter:${key}`,
    defaultState,
  })

  const applyFilter = useCallback(
    (nextFilter: Partial<ProductBoardItemsFilterInput>) => {
      setPersistentFilter((prevFilter) =>
        removeEmptyFields<ProductBoardItemsFilterInput>({ ...prevFilter, ...nextFilter })
      )
    },
    [setPersistentFilter]
  )

  const resetFilter = useCallback(
    (values?: ProductBoardItemsFilterInput) => {
      setPersistentFilter(values || {})
    },
    [setPersistentFilter]
  )

  const filterValue = useMemo(() => {
    const accountsFilter = pickValidFields(persistentFilter?.accountsFilter || {}, [
      {
        keys: [
          'createdAtGte',
          'createdAtLte',
          'updatedAtGte',
          'updatedAtLte',
          'accountValueGte',
          'accountValueLte',
          'annualRevenueGte',
          'annualRevenueLte',
          'employeesGte',
          'employeesLte',
          'type',
          'industry',
        ],
        validator: (v) => !isNil(v),
      },
      {
        keys: ['type', 'industry'],
        validator: (v) => {
          return Array.isArray(v) && v.length > 0
        },
      },
    ])
    const baseFilter = pickValidFields(persistentFilter, [
      {
        key: 'query',
        validator: (v) => !isEmpty(v),
      },
      {
        keys: ['endDateLte', 'endDateGte'],
        validator: (v) => !isNil(v),
      },
      {
        keys: [
          'roadmapIds',
          'tagIds',
          'priority',
          'productLineTagIds',
          'statusIds',
          'releaseIds',
          'assigneeIds',
          'competitorIds',
          'feedbackPostIds',
          'insightIds',
          'personaIds',
          'launchIds',
          'accountIds',
          'dealIds',
        ],
        validator: (v) => Array.isArray(v),
      },
    ])

    return {
      ...baseFilter,
      ...(isEmpty(accountsFilter) ? {} : { accountsFilter }),
    }
  }, [persistentFilter])

  return [filterValue, applyFilter, resetFilter] as const
}

export default useProductBoardItemsFilter
